import {
  Button,
  ComponentMultiStyleConfig,
  Flex,
  Text,
  useMultiStyleConfig,
} from "@chakra-ui/react";
import React, { useCallback, useMemo } from "react";
import { ComponentStyleProps } from "../../../../../../lib/styles/props/component-style-props";
import { hasValue, isNullish } from "../../../../../../lib/util/common-util";
import { SpaceProps } from "@chakra-ui/styled-system";

export type CMSearchResultCellJsonDiffModalProps = {
  value?: {
    before: any;
    after: any;
  };

  options?: {
    beforeLabel?: string;
    afterLabel?: string;
    closeButtonLabel?: string;
    jsonIndent?: number;
  };

  onClose: () => void;
} & ComponentStyleProps;

export const CMSearchResultCellJsonDiffModal = ({
  value,
  options,

  onClose,

  sx,
  ...rest
}: CMSearchResultCellJsonDiffModalProps) => {
  const styles = useMultiStyleConfig("CMSearchResultCellJsonDiffModalStyles");

  const containerStyles = useMemo(
    () => ({ ...styles.container, ...sx }),
    [styles.container, sx],
  );

  const jsonIndent: number = useMemo(
    () => options?.jsonIndent ?? 4,
    [options?.jsonIndent],
  );

  const beforeValueText = useMemo(() => {
    if (isNullish(value) || isNullish(value?.before)) return "";
    return JSON.stringify(value.before, undefined, jsonIndent);
  }, [value, jsonIndent]);
  const afterValueText = useMemo(() => {
    if (isNullish(value) || isNullish(value?.after)) return "";
    return JSON.stringify(value.after, undefined, jsonIndent);
  }, [value, jsonIndent]);

  const getLabelAndValue = useCallback(
    (label: string | undefined, v: string, ml?: SpaceProps["ml"]) => {
      return (
        <Flex
          flex={1}
          minW={0}
          ml={ml}
          direction={"column"}
          alignItems={"flex-start"}
        >
          {hasValue(label) && <Text sx={styles.label}>{label}</Text>}
          <Text sx={styles.value}>{v}</Text>
        </Flex>
      );
    },
    [styles.label, styles.value],
  );

  const beforeNode = useMemo(() => {
    return getLabelAndValue(options?.beforeLabel, beforeValueText);
  }, [getLabelAndValue, options?.beforeLabel, beforeValueText]);
  const afterNode = useMemo(() => {
    return getLabelAndValue(options?.afterLabel, afterValueText, "30px");
  }, [getLabelAndValue, options?.afterLabel, afterValueText]);

  return (
    <Flex direction={"column"} sx={containerStyles} {...rest}>
      <Flex direction={"row"} sx={styles.main}>
        {beforeNode}
        {/*<Divider orientation={"vertical"} />*/}
        {afterNode}
      </Flex>
      <Flex direction={"column"} sx={styles.footer}>
        <Button sx={styles.closeButton} size={"sm"} onClick={onClose}>
          {options?.closeButtonLabel ?? "Close"}
        </Button>
      </Flex>
    </Flex>
  );
};

export const CMSearchResultCellJsonDiffModalStyles: ComponentMultiStyleConfig =
  {
    parts: ["container", "main", "label", "value", "footer", "closeButton"],
    baseStyle: {
      container: {
        alignItems: "stretch",
        overflow: "hidden",
      },
      main: {
        maxH: "400px",
        pt: "20px",
        pr: "10px",
        pb: "10px",
        pl: "20px",
        overflowY: "auto",
      },
      label: {
        mb: "10px",

        fontWeight: "bold",
        fontSize: "md",
        color: "gray.900",
      },
      value: {
        fontSize: "sm",
        color: "gray.900",
        whiteSpace: "pre-wrap",
        overflowWrap: "anywhere",
      },
      footer: {
        alignSelf: "stretch",
        pt: "10px",
        pr: "10px",
        pb: "10px",
      },
      closeButton: {
        alignSelf: "flex-end",
      },
    },
  };
